import React from "react";
import PropTypes from "prop-types";
import { Icon, Input, InputNumber } from "antd";

const DASH = "—";

const formatRupees = v =>
    v === "" || v === null || v === undefined
        ? ""
        : `₹ ${Number(v).toLocaleString("en-IN")}`;
const parseRupees = v => String(v).replace(/[^0-9.]/g, "");

// A row is empty when every column is blank — those are dropped on submit rather
// than sent to the API as a row of nulls.
export function isEmptyRow(row, columns) {
    return columns.every(col => {
        const v = row ? row[col.key] : null;
        return v === null || v === undefined || v === "";
    });
}

function displayCell(value, col) {
    if (value === null || value === undefined || value === "") {
        return DASH;
    }
    if (col.type === "currency") {
        return formatRupees(value);
    }
    if (col.type === "number") {
        return col.suffix ? `${value}${col.suffix}` : String(value);
    }
    return String(value);
}

/**
 * A repeating group of the Credit-TVR form — one row per list item, driven by a
 * `columns` config. The API replaces lists wholesale on PATCH, so the whole array
 * is submitted together, not row-by-row.
 *
 * Locked (not editing) it renders a plain table; in edit mode each cell becomes an
 * input with a per-row delete and an "Add row" action.
 */
class TvrRepeater extends React.Component {
    rows() {
        const { value } = this.props;
        return Array.isArray(value) ? value : [];
    }

    emit(rows) {
        this.props.onChange(rows);
    }

    setCell = (rowIndex, colKey, cellValue) => {
        const rows = this.rows().map((row, i) =>
            i === rowIndex
                ? { ...row, [colKey]: cellValue === undefined ? "" : cellValue }
                : row
        );
        this.emit(rows);
    };

    addRow = () => {
        const blank = {};
        this.props.columns.forEach(col => {
            blank[col.key] = "";
        });
        this.emit(this.rows().concat(blank));
    };

    removeRow = rowIndex =>
        this.emit(this.rows().filter((_, i) => i !== rowIndex));

    renderCell(row, rowIndex, col) {
        const value = row[col.key];
        const common = {
            className: "cc-tvr-rep__input",
            disabled: this.props.saving,
            placeholder: col.label
        };
        if (col.type === "currency") {
            return (
                <InputNumber
                    {...common}
                    value={value === "" || value == null ? undefined : Number(value)}
                    min={0}
                    formatter={formatRupees}
                    parser={parseRupees}
                    style={{ width: "100%" }}
                    onChange={v => this.setCell(rowIndex, col.key, v)}
                />
            );
        }
        if (col.type === "number") {
            return (
                <InputNumber
                    {...common}
                    value={value === "" || value == null ? undefined : Number(value)}
                    min={col.min}
                    max={col.max}
                    formatter={
                        col.suffix
                            ? v =>
                                v === "" || v == null ? "" : `${v}${col.suffix}`
                            : undefined
                    }
                    parser={
                        col.suffix
                            ? v => String(v).replace(col.suffix, "")
                            : undefined
                    }
                    style={{ width: "100%" }}
                    onChange={v => this.setCell(rowIndex, col.key, v)}
                />
            );
        }
        return (
            <Input
                {...common}
                value={value == null ? "" : String(value)}
                onChange={e => this.setCell(rowIndex, col.key, e.target.value)}
            />
        );
    }

    renderReadOnly() {
        const { columns } = this.props;
        const rows = this.rows().filter(row => !isEmptyRow(row, columns));
        if (!rows.length) {
            return (
                <div className={"cc-tvr-readonly cc-tvr-readonly--empty"}>
                    {DASH}
                </div>
            );
        }
        return (
            <div className={"cc-tvr-rep cc-tvr-rep--locked"}>
                <table className={"cc-tvr-rep__table"}>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {columns.map(col => (
                                    <td key={col.key}>
                                        {displayCell(row[col.key], col)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { columns, editing, saving } = this.props;
        if (!editing) {
            return this.renderReadOnly();
        }
        const rows = this.rows();
        return (
            <div className={"cc-tvr-rep"}>
                <table className={"cc-tvr-rep__table"}>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                            <th className={"cc-tvr-rep__rowaction"} />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {columns.map(col => (
                                    <td key={col.key}>
                                        {this.renderCell(row, i, col)}
                                    </td>
                                ))}
                                <td className={"cc-tvr-rep__rowaction"}>
                                    <button
                                        type={"button"}
                                        className={"cc-tvr-rep__del"}
                                        onClick={() => this.removeRow(i)}
                                        disabled={saving}
                                        title={"Remove row"}
                                    >
                                        <Icon type={"close"} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type={"button"}
                    className={"cc-tvr-rep__add"}
                    onClick={this.addRow}
                    disabled={saving}
                >
                    <Icon type={"plus"} /> {"Add row"}
                </button>
            </div>
        );
    }
}

TvrRepeater.propTypes = {
    // [{ key, label, type?: "text" | "number" | "currency", suffix?, min?, max? }]
    columns: PropTypes.array.isRequired,
    value: PropTypes.array,
    editing: PropTypes.bool,
    saving: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

export default TvrRepeater;
